import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  SingleStepContainer,
  SingleStepIllustrationContainer,
  StepsContainer
} from './JourneyWidget.styled';
import {
  ButtonsContainer,
  IconContainer,
  TextRow,
  WidgetTitleContainer
} from '../Widgets/Widgets.styled';
import { CheckCircle, CircleDot, Route } from 'assets/icons/Icons';
import { Button } from 'components/v2/Buttons/Button/Button';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import { buildCompactJourney } from 'screens/MyJourney/journeySteps/journeySteps.utils';
import { analytics } from 'utilities/analytics';
import { pageUrl } from 'utilities/pageUrl';

import { JourneyResponse } from '../Dashboard.types';
import { DashboardWidgetList } from '../DashboardSettings/DashboardSettings.types';
import { isHiddenWidget } from '../DashboardSettings/DashboardSettings.utils';
import { WidgetBase } from '../Widgets/WidgetBase';
import { showJourneyWidget } from '../Widgets/Widgets.utils';

interface Props {
  journeyData: JourneyResponse | undefined;
  hiddenWidgets?: DashboardWidgetList;
}

export const JourneyWidget = ({ journeyData, hiddenWidgets }: Props) => {
  const navigate = useNavigate();

  if (!showJourneyWidget(journeyData) || isHiddenWidget(hiddenWidgets, 'myJourney')) return null;

  // just for Ts to know that journeyData is not undefined
  if (!journeyData) return null;

  const { cyclePlan, patientTodos, results, appointments, journeyType } = journeyData;

  const journey = buildCompactJourney({
    appointments,
    patientTodos,
    results,
    triggerInstruction: null,
    journeyType,
    cyclePlan
  });

  if (!journey) return null;

  return (
    <WidgetBase>
      <IconContainer $iconWidth={48} $iconHeight={48}>
        <Route type="solid" />
      </IconContainer>
      <WidgetTitleContainer>
        <Heading tag="div" styledAs="h2">
          My Journey
        </Heading>
      </WidgetTitleContainer>
      <TextRow>
        <Text fontStyle="semibold">
          Track your progress and keep abreast of what&apos;s to come in your journey.
        </Text>
      </TextRow>
      <StepsContainer>
        {journey.steps.map((step, index) => {
          const completed = step.isComplete;
          const last = index === journey.steps.length - 1;
          return (
            <SingleStepContainer key={step.title} $last={last}>
              <SingleStepIllustrationContainer $completed={completed} $last={last}>
                {completed ? <CheckCircle type="solid" /> : <CircleDot type="solid" />}
              </SingleStepIllustrationContainer>
              <Text fontStyle={completed ? 'bold' : 'regular'} size="lg">
                {step.title}
              </Text>
            </SingleStepContainer>
          );
        })}
      </StepsContainer>

      <ButtonsContainer>
        <Button
          label="View more"
          size="lg"
          category="primary-dark"
          onClick={() => {
            analytics.track(analytics.EVENTS.MY_JOURNEY_VIEW_MORE);
            navigate(pageUrl.myJourney());
          }}
        />
      </ButtonsContainer>
    </WidgetBase>
  );
};
